import React, { useState, useEffect } from 'react';
import { DollarSign, ArrowLeftRight } from 'lucide-react';
import { SEO } from '../components/SEO';

interface CurrencyState {
  amount: string;
  fromCurrency: string;
  toCurrency: string;
  result: number | null;
}

const exchangeRates: Record<string, Record<string, number>> = {
  'EUR': {
    'BAM': 1.95583,
    'RSD': 117.20,
    'HRK': 7.5345,
    'MKD': 61.50,
    'ALL': 103.50,
  },
  'BAM': {
    'EUR': 0.511292,
    'RSD': 59.92,
    'HRK': 3.85,
    'MKD': 31.45,
    'ALL': 52.92,
  },
  'RSD': {
    'EUR': 0.008532,
    'BAM': 0.016689,
    'HRK': 0.0643,
    'MKD': 0.525,
    'ALL': 0.883,
  },
  'HRK': {
    'EUR': 0.132722,
    'BAM': 0.259740,
    'RSD': 15.55,
    'MKD': 8.16,
    'ALL': 13.74,
  },
  'MKD': {
    'EUR': 0.016260,
    'BAM': 0.031797,
    'RSD': 1.907,
    'HRK': 0.1225,
    'ALL': 1.684,
  },
  'ALL': {
    'EUR': 0.009662,
    'BAM': 0.018897,
    'RSD': 1.133,
    'HRK': 0.0728,
    'MKD': 0.594,
  },
};

const currencyNames: Record<string, string> = {
  'EUR': 'Euro',
  'BAM': 'Konvertibilna marka',
  'RSD': 'Srpski dinar',
  'HRK': 'Hrvatska kuna',
  'MKD': 'Makedonski denar',
  'ALL': 'Albanski lek',
};

const currencySymbols: Record<string, string> = {
  'EUR': '€',
  'BAM': 'KM',
  'RSD': 'RSD',
  'HRK': 'kn',
  'MKD': 'ден',
  'ALL': 'L',
};

function KonverterValuta() {
  const [values, setValues] = useState<CurrencyState>({
    amount: '',
    fromCurrency: 'EUR',
    toCurrency: 'BAM',
    result: null
  });

  const calculateConversion = () => {
    const amount = parseFloat(values.amount);
    
    if (isNaN(amount)) {
      return null;
    }

    if (values.fromCurrency === values.toCurrency) {
      return amount;
    }

    const rate = exchangeRates[values.fromCurrency][values.toCurrency];
    return amount * rate;
  };

  const handleInputChange = (field: keyof CurrencyState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const swapCurrencies = () => {
    setValues(prev => ({
      ...prev,
      fromCurrency: prev.toCurrency,
      toCurrency: prev.fromCurrency,
      result: null
    }));
  };

  useEffect(() => {
    if (values.amount) {
      const result = calculateConversion();
      setValues(prev => ({
        ...prev,
        result: result !== null ? parseFloat(result.toFixed(2)) : null
      }));
    }
  }, [values.amount, values.fromCurrency, values.toCurrency]);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Konverter Valuta",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Online konverter valuta za balkanske zemlje. Pretvorite EUR, BAM, RSD i druge valute regije.",
    "url": "https://online-kalkulator.com/konverter-valuta",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Konverzija između više valuta",
      "Ažurni tečajevi",
      "Precizni izračuni",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Konverter Valuta | Pretvarač Valuta za Balkansku Regiju"
        description="Online konverter valuta za balkanske zemlje. Pretvorite EUR, BAM, RSD i druge valute regije. Ažurni tečajevi i precizni izračuni."
        canonicalUrl="/konverter-valuta"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <DollarSign className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Konverter Valuta</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Converter Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Iznos
                </label>
                <input
                  type="number"
                  value={values.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Unesite iznos"
                />
              </div>

              {/* Currency Selection */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iz valute
                  </label>
                  <select
                    value={values.fromCurrency}
                    onChange={(e) => handleInputChange('fromCurrency', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(currencyNames).map(([code, name]) => (
                      <option key={code} value={code}>
                        {name} ({code})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={swapCurrencies}
                  className="mt-6 p-2 text-gray-500 hover:text-blue-600 transition"
                >
                  <ArrowLeftRight className="w-6 h-6" />
                </button>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    U valutu
                  </label>
                  <select
                    value={values.toCurrency}
                    onChange={(e) => handleInputChange('toCurrency', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(currencyNames).map(([code, name]) => (
                      <option key={code} value={code}>
                        {name} ({code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Result */}
              {values.result !== null && (
                <div className="border-t pt-4">
                  <div className="text-center">
                    <div className="text-gray-600 mb-2">
                      {formatCurrency(parseFloat(values.amount), values.fromCurrency)} {currencySymbols[values.fromCurrency]} =
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(values.result, values.toCurrency)} {currencySymbols[values.toCurrency]}
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      1 {values.fromCurrency} = {formatCurrency(exchangeRates[values.fromCurrency][values.toCurrency], values.toCurrency)} {values.toCurrency}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Konverteru Valuta</h2>
                <p className="text-gray-600 leading-relaxed">
                  Konverter valuta je koristan alat koji vam omogućuje brzo i jednostavno pretvaranje
                  između različitih valuta koje se koriste u balkanskoj regiji. Podržane su sve glavne
                  valute regije, uključujući euro, konvertibilnu marku, srpski dinar i druge.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Podržane Valute</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(currencyNames).map(([code, name]) => (
                    <div key={code} className="flex items-center space-x-2">
                      <span className="font-medium text-gray-700">{code}:</span>
                      <span className="text-gray-600">{name}</span>
                      <span className="text-gray-500">({currencySymbols[code]})</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite iznos koji želite konvertirati</li>
                  <li>Odaberite početnu valutu (iz koje konvertirate)</li>
                  <li>Odaberite ciljanu valutu (u koju konvertirate)</li>
                  <li>Rezultat će se automatski izračunati</li>
                  <li>Koristite gumb za zamjenu valuta za brzu zamjenu smjera konverzije</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Tečajevi su informativne prirode i ažuriraju se periodično</li>
                  <li>Svi iznosi su zaokruženi na dvije decimale</li>
                  <li>Konverzije se vrše prema srednjem tečaju</li>
                  <li>Za točne tečajeve kod stvarnih transakcija, provjerite s vašom bankom</li>
                  <li>Tečajevi mogu varirati ovisno o tržišnim uvjetima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Tečajne Liste</h2>
                <p className="text-gray-600 mb-4">
                  Tečajevi se temelje na srednjim vrijednostima i redovito se ažuriraju. Za preciznije
                  informacije o tečajevima, preporučujemo da posjetite službene stranice centralnih
                  banaka:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Europska centralna banka (ECB)</li>
                  <li>Centralna banka Bosne i Hercegovine (CBBH)</li>
                  <li>Narodna banka Srbije (NBS)</li>
                  <li>Hrvatska narodna banka (HNB)</li>
                  <li>Narodna banka Republike Sjeverne Makedonije (NBRSM)</li>
                  <li>Banka Albanije (BoA)</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KonverterValuta;